import api from '../api'

// The messenger loads this long after the page has, when the browser is next idle (at most IDLE_TIMEOUT_MS later)
const IDLE_DELAY_MS = 3000
const IDLE_TIMEOUT_MS = 2000

const intercom = (data) => ({
    // --- Props and state ---
    appId: data.apiKey,
    apiBase: data.apiBase,
    // Until the bubble is offered
    loading: true,
    widgetOpen: false,
    region: data.region,
    language: data.language,
    userId: null,
    token: null,
    openedAt: null,
    unreadCount: 0,
    unreadBaselineSet: false,
    emailSupplied: false,
    unavailable: false,
    identity: null,
    starting: null,
    openRequested: false,

    init() {
        // No Intercom app configured (local environments)
        if (!this.appId) {
            window.Alpine.nextTick(() => this.onUnavailable())
            return
        }

        // Still fetched as the page starts: for a new visitor it writes the session cookie, and fetched later it
        // could overlap a click that writes the cookie too (the first Add To Basket) and undo it
        this.identity = this.fetchIdentity()

        // The bubble and the "Chat With Us" buttons are ours, so they show straight away. The messenger loads
        // once the page has loaded and gone quiet, or as soon as someone asks for chat
        this.loading = false
        window.Alpine.nextTick(() => window.dispatchEvent(new CustomEvent('livechat-ready')))
        this.startWhenIdle()
    },

    startWhenIdle() {
        const whenIdle = () => setTimeout(() => {
            if (window.requestIdleCallback) {
                window.requestIdleCallback(() => this.start(), { timeout: IDLE_TIMEOUT_MS })
            } else {
                this.start()
            }
        }, IDLE_DELAY_MS)

        if (document.readyState === 'complete') {
            whenIdle()
        } else {
            window.addEventListener('load', whenIdle, { once: true })
        }
    },

    // Loads the messenger once, with the identity fetched in init()
    start() {
        if (!this.starting) {
            this.starting = this.identity.then(() => this.loadIntercom())
        }

        return this.starting
    },

    async fetchIdentity() {
        const identity = await api.getIntercomIdentity()
        if (!identity || !identity.token) return
        this.userId = identity.user_id ?? null
        this.token = identity.token
    },

    loadIntercom() {
        if (window.Intercom && typeof window.Intercom === 'function') {
            this.onReady()
            return
        }

        // No Intercom app configured (local environments)
        if (!this.appId) {
            this.onUnavailable()
            return
        }

        // Chat is ready when the messenger script has actually loaded, not when the queueing stub below exists:
        // ad blockers often block Intercom, and then the bubble and every "Chat With Us" button did nothing
        const component = this

        window.intercomSettings = {
            api_base: decodeURIComponent(this.apiBase),
            app_id: this.appId,
            hide_default_launcher: true,
            region: this.region,
            language: this.language,
            intercom_user_jwt: this.token,
            ...this.getUserData()
        };

        (function () {
            var w = window;
            var ic = w.Intercom;
            if (typeof ic === "function") {
                ic('reattach_activator');
                ic('update', w.intercomSettings);
            } else {
                var d = document;
                var i = function () { i.c(arguments); };
                i.q = [];
                i.c = function (args) { i.q.push(args); };
                w.Intercom = i;
                var l = function () {
                    var s = d.createElement('script');
                    s.type = 'text/javascript';
                    s.async = true;
                    s.src = 'https://widget.intercom.io/widget/' + window.intercomSettings.app_id;
                    s.onload = () => component.onReady()
                    s.onerror = () => component.onUnavailable()
                    var x = d.getElementsByTagName('script')[0];
                    x.parentNode.insertBefore(s, x);
                };
                if (document.readyState === 'complete') {
                    l();
                } else if (w.attachEvent) {
                    w.attachEvent('onload', l);
                } else {
                    w.addEventListener('load', l, false);
                }
            }
        })()

        window.Intercom('update', {
            region: this.region,
            language: this.language
        });
    },

    getUserData() {
        if (!this.token) return {}

        const user = Alpine.store('user')
        const storeUserId = user.id_customer ? String(user.id_customer) : null
        const userId = this.userId || user.id_customer

        if (!user.isLoggedIn || (this.userId && storeUserId !== this.userId)) {
            return userId ? { user_id: userId } : {}
        }

        return {
            user_id: userId,
            name: user.firstname,
            email: user.email,
            created_at: user.created_at,
        }
    },

    onReady() {
        this.loading = false;
        window.dispatchEvent(new CustomEvent('livechat-ready'))

        window.Intercom('onShow', () => {
            this.widgetOpen = true;
            this.openedAt = Date.now();
            this.pushLiveChatEvent('gtm_livechat_opened');
        });

        window.Intercom('onHide', () => {
            // Opening the widget says very little on its own. The time spent
            // with it open is what separates a real conversation from someone
            // clicking the bubble and closing it again.
            const seconds = this.openedAt
                ? Math.round((Date.now() - this.openedAt) / 1000)
                : null;

            this.widgetOpen = false;
            this.openedAt = null;

            this.pushLiveChatEvent('gtm_livechat_closed', {
                livechat_duration_seconds: seconds,
            });
        });

        // The visitor handed over their email inside the chat. This is the
        // strongest signal Intercom exposes - it is a captured lead.
        window.Intercom('onUserEmailSupplied', () => {
            if (this.emailSupplied) return;
            this.emailSupplied = true;
            this.pushLiveChatEvent('gtm_livechat_email_supplied');
        });

        // Fires whenever the unread count changes, and fires once on load with
        // whatever is already unread. Only an increase after that first call
        // means an operator has replied - taking the first call as a baseline
        // stops a returning visitor with unread messages being counted as a
        // fresh reply on every page they open.
        window.Intercom('onUnreadCountChange', (count) => {
            const unread = parseInt(count, 10) || 0;

            if (!this.unreadBaselineSet) {
                this.unreadBaselineSet = true;
                this.unreadCount = unread;
                return;
            }

            if (unread > this.unreadCount) {
                this.pushLiveChatEvent('gtm_livechat_reply_received');
            }

            this.unreadCount = unread;
        });
    },

    pushLiveChatEvent(event, payload = {}) {
        const detail = { event };

        Object.keys(payload).forEach((key) => {
            if (payload[key] !== null && payload[key] !== undefined) {
                detail[key] = payload[key];
            }
        });

        window.dispatchEvent(new CustomEvent('gtmdata', { detail }));
    },

    // The messenger couldn't load, so chat buttons open the contact form instead and the bubble hides.
    // A visitor who asked for chat while it was loading gets the contact form
    onUnavailable() {
        this.unavailable = true
        window.dispatchEvent(new CustomEvent('livechat-unavailable'))

        if (this.openRequested) {
            this.openContactForm()
        }
    },

    openContactForm() {
        const general = window.Alpine.store('general')
        general.showOverlay = true
        general.showContactUsModal = true
        general.contactType = 'help'
    },

    openWidget() {
        if (this.unavailable) {
            this.openContactForm()
            return
        }

        this.openRequested = true

        // Loads the messenger if it hasn't started yet. Before its script has loaded, the stub queues this
        // and the messenger opens once it's ready
        this.start().then(() => {
            if (!this.unavailable && window.Intercom) window.Intercom('show')
        })
    },
    closeWidget() {
        if (window.Intercom) window.Intercom('hide')
    },
});

document.addEventListener('alpine:init', () => {
    window.Alpine.data('intercom', intercom)
})
